import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { compressImage } from '../lib/image';
import { Save, Upload, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
];

export const I18nField = ({ label, value, onChange, multiline = false }) => {
  const v = value || {};
  return (
    <div>
      <Label className="mb-2 block text-sm">{label}</Label>
      <div className="space-y-2">
        {LANGS.map((l) => (
          <div key={l.code} className="flex gap-2">
            <span className="w-12 text-xs text-gray-500 pt-3 uppercase">{l.code}</span>
            {multiline ? (
              <Textarea
                rows={2}
                value={v[l.code] || ''}
                onChange={(e) => onChange({ ...v, [l.code]: e.target.value })}
                placeholder={l.label}
                dir={l.code === 'ar' ? 'rtl' : 'ltr'}
              />
            ) : (
              <Input
                value={v[l.code] || ''}
                onChange={(e) => onChange({ ...v, [l.code]: e.target.value })}
                placeholder={l.label}
                className="h-10"
                dir={l.code === 'ar' ? 'rtl' : 'ltr'}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const ImagePicker = ({ label, value, onChange, hint, mode = 'contain', big = false }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = await compressImage(file, big ? { maxW: 1800, maxH: 1800, quality: 0.85 } : { maxW: 1000, maxH: 1000, quality: 0.85 });
      onChange(data);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label className="mb-2 block text-sm">{label}</Label>
      <div className="flex items-start gap-4">
        <div className={`${big ? 'w-40 h-32' : 'w-28 h-28'} rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0`}>
          {value ? (
            <img
              src={value}
              alt=""
              className={`w-full h-full ${mode === 'cover' ? 'object-cover' : 'object-contain'}`}
              style={mode === 'contain' ? { mixBlendMode: 'multiply' } : {}}
            />
          ) : (
            <span className="text-xs text-gray-400">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-gray-200 hover:border-[#c4477a]/40 cursor-pointer text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
          </label>
          {value && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          )}
          {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const AdminHero = () => {
  const { refresh } = useSiteConfig();
  const [data, setData] = useState({
    welcome: {}, title: {}, description: {}, cta_text: {},
    logo_image: null, bg_image: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/hero').then((r) => setData({ ...data, ...r.data })).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/hero', data);
      toast.success('Saved!');
      refresh();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Hero Section</h1>
        <p className="text-gray-500 text-sm">Main landing area at the top of the homepage.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        <I18nField label="Welcome Tag (small text above title)" value={data.welcome} onChange={(v) => setData({ ...data, welcome: v })} />
        <I18nField label="Title" value={data.title} onChange={(v) => setData({ ...data, title: v })} />
        <I18nField label="Description" value={data.description} onChange={(v) => setData({ ...data, description: v })} multiline />
        <I18nField label="Button Text" value={data.cta_text} onChange={(v) => setData({ ...data, cta_text: v })} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <ImagePicker
          label="Hero Logo / Image (right side)"
          value={data.logo_image}
          onChange={(v) => setData({ ...data, logo_image: v })}
          hint="Logo image displayed on the right side of hero"
        />
        <ImagePicker
          label="Background Image (optional)"
          value={data.bg_image}
          onChange={(v) => setData({ ...data, bg_image: v })}
          hint="Optional background image (otherwise gradient is shown)"
          mode="cover"
          big
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-12 px-8 gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminHero;
